import api from './api'; // Import the shared api instance
import JSZip from 'jszip'; // Import jszip for ZIP file generation

const parseBoardData = (board) => {
    if (board && typeof board.data === 'string') {
        try {
            board.data = JSON.parse(board.data);
        } catch (e) {
            console.error('Error parsing board data JSON:', e);
            board.data = { columns: {} }; // Default to empty if parsing fails
        }
    }
    // Ensure columns is an object, even if it's an empty array from old data
    if (board && board.data && Array.isArray(board.data.columns)) {
        board.data.columns = {};
    }
    return board;
};

const getBoards = async (userId) => {
    const response = await api.get(`/users/${userId}/boards`); // x-user-id removed
    return response.data.data.map(parseBoardData);
};

const getBoard = async (boardId, noCache = false) => {
    const config = {};
    if (noCache) {
        config.params = { _: new Date().getTime() };
    }
    const response = await api.get(`/boards/${boardId}`, config);
    return parseBoardData(response.data.data);
};

const createBoard = async (userId, name, data, collection) => {
    const payload = { user_id: userId, name, data: data };
    if (collection !== null) {
        payload.collection = collection;
    }
    const response = await api.post(`/boards`, payload);
    return response.data.data;
};

const updateBoard = async (boardId, name, data, collection) => {
    const payload = { name, data: data };
    if (collection !== null) {
        payload.collection = collection;
    }
    const response = await api.put(`/boards/${boardId}`, payload);
    return response.data.data;
};

const deleteBoard = async (boardId) => {
    const response = await api.delete(`/boards/${boardId}`);
    return response.data;
};

const exportBoardJson = async (boardId, userId) => {
    const response = await api.get(`/boards/${boardId}/export/json`, {
        responseType: 'blob', // Important for file downloads
        // headers: { 'x-user-id': userId }, // x-user-id removed
    });
    // Create a Blob from the response data
    const blob = new Blob([response.data], { type: 'application/json' });
    // Create a link element, set its href to the Blob, and click it to trigger download
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = response.headers['content-disposition'].split('filename=')[1].replace(/"/g, ''); // Extract filename from headers
    link.click();
    window.URL.revokeObjectURL(link.href); // Clean up the URL object
    return response.data;
};

const exportBoardCsv = async (boardId, userId) => {
    const response = await api.get(`/boards/${boardId}/export/csv`, {
        responseType: 'blob', // Important for file downloads
        // headers: { 'x-user-id': userId }, // x-user-id removed
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = response.headers['content-disposition'].split('filename=')[1].replace(/"/g, '');
    link.click();
    window.URL.revokeObjectURL(link.href);
    return response.data;
};

const generateBoardMarkdown = (boardName, boardDescription, boardData) => {
    let markdown = `# Board Name: ${boardName}\n\n`;
    if (boardDescription) {
        markdown += `${boardDescription}\n\n`;
    }

    const tasksMap = {};
    if (boardData.columns) {
        Object.values(boardData.columns).forEach((col) => {
            if (col.tasks) {
                col.tasks.forEach((t) => (tasksMap[t.id] = t));
            }
        });
    }

    if (boardData.columnOrder) {
        boardData.columnOrder.forEach((columnId) => {
            const column = boardData.columns[columnId];
            if (!column) return;
            markdown += `## Column: ${column.title}\n\n`;

            if (column.tasks && Array.isArray(column.tasks)) {
                column.tasks.forEach((task) => {
                    if (!task) return;

                    const isCompleted = task.completed ? 'x' : ' ';
                    markdown += `- [${isCompleted}] **${task.content}** (#${task.displayId})\n`;

                    if (task.dueDate) {
                        const date = new Date(task.dueDate).toISOString().split('T')[0];
                        markdown += `  - **Due:** ${date}\n`;
                    }

                    if (task.description) {
                        const description = task.description.replace(/\n/g, '\n    > ');
                        markdown += `  - **Description:**\n    > ${description}\n`;
                    }

                    if (task.subtasks && task.subtasks.length > 0) {
                        markdown += `  - **Subtasks:**\n`;
                        task.subtasks.forEach((subtaskId) => {
                            const subtask = tasksMap[subtaskId];
                            if (subtask) {
                                const isSubtaskCompleted = subtask.completed ? 'x' : ' ';
                                markdown += `    - [${isSubtaskCompleted}] ${subtask.content} (#${subtask.displayId})\n`;
                            }
                        });
                    }

                    if (task.comments && task.comments.length > 0) {
                        markdown += `  - **Comments:**\n`;
                        task.comments.forEach((comment) => {
                            const date = new Date(comment.createdAt).toISOString().split('T')[0];
                            const content = comment.content.replace(/\n/g, ' '); // Replace newlines in comments for list format
                            markdown += `    - **${comment.username} (${date}):** ${content}\n`;
                        });
                    }
                    markdown += '\n';
                });
            }
        });
    }

    if (boardData.archiveHistory && boardData.archiveHistory.length > 0) {
        markdown += `## Archived Tasks\n\n`;
        boardData.archiveHistory.forEach((archiveEntry) => {
            markdown += `### ${archiveEntry.date}\n\n`;
            archiveEntry.tasks.forEach((task) => {
                const isCompleted = task.completed ? 'x' : ' ';
                markdown += `- [${isCompleted}] **${task.content}** (#${task.displayId})\n`;
                if (task.columnTitle) {
                    markdown += `  - **Original Column:** ${task.columnTitle}\n`;
                }
                if (task.dueDate) {
                    const date = new Date(task.dueDate).toISOString().split('T')[0];
                    markdown += `  - **Due:** ${date}\n`;
                }
                if (task.description) {
                    const description = task.description.replace(/\n/g, '\n    > ');
                    markdown += `  - **Description:**\n    > ${description}\n`;
                }
                if (task.subtasks && task.subtasks.length > 0) {
                    markdown += `  - **Subtasks:**\n`;
                    task.subtasks.forEach((subtaskId) => {
                        const subtask = tasksMap[subtaskId];
                        if (subtask) {
                            const isSubtaskCompleted = subtask.completed ? 'x' : ' ';
                            markdown += `    - [${isSubtaskCompleted}] ${subtask.content} (#${subtask.displayId})\n`;
                        }
                    });
                }
                if (task.comments && task.comments.length > 0) {
                    markdown += `  - **Comments:**\n`;
                    task.comments.forEach((comment) => {
                        const date = new Date(comment.createdAt).toISOString().split('T')[0];
                        const content = comment.content.replace(/\n/g, ' ');
                        markdown += `    - **${comment.username} (${date}):** ${content}\n`;
                    });
                }
                markdown += '\n';
            });
        });
    }

    if (boardData.contacts && boardData.contacts.length > 0) {
        markdown += `## Contacts\n\n`;
        boardData.contacts.forEach((contact) => {
            if (!contact) return;
            markdown += `### ${contact.name}\n`;
            if (contact.title || contact.company) {
                markdown += `- **Title/Company:** ${contact.title || ''}${contact.title && contact.company ? ' at ' : ''}${contact.company || ''}\n`;
            }
            if (contact.email) {
                markdown += `- **Email:** ${contact.email}\n`;
            }
            if (contact.phone) {
                markdown += `- **Phone:** ${contact.phone}\n`;
            }
            markdown += `- **Status:** ${contact.status}\n\n`;
        });
    }

    return markdown;
};

const downloadMarkdownFile = (markdownContent, boardName) => {
    const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    const date = new Date().toISOString().split('T')[0];
    link.download = `${boardName}-export-${date}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
};

const exportBoardMarkdown = (boardName, boardDescription, boardData) => {
    const markdownContent = generateBoardMarkdown(boardName, boardDescription, boardData);
    downloadMarkdownFile(markdownContent, boardName);
};

const importBoardJson = async (boardData, userId, collection) => {
    const { name, data } = boardData; // Extract name and data from the imported JSON
    const payload = {
        name,
        data,
        user_id: userId, // Backend expects user_id, not userId
    };
    if (collection) {
        payload.collection = collection;
    }
    const response = await api.post(`/boards/import/json`, payload);
    return response.data;
};

const importBoardCsv = async (csvData, boardName) => {
    const response = await api.post(`/boards/import/csv`, { csvData, boardName });
    return response.data;
};

const exportAllBoardsMarkdownAsZip = async (userId) => {
    try {
        const allBoards = await getBoards(userId);
        const zip = new JSZip();
        const date = new Date().toISOString().split('T')[0];

        for (const board of allBoards) {
            const markdown = generateBoardMarkdown(board.name, board.data.description, board.data);
            zip.file(`${board.name}-export-${date}.md`, markdown);
        }

        const content = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(content);
        link.download = `all-boards-markdown-export-${date}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error('Error exporting all boards as Markdown ZIP:', error);
        throw error;
    }
};

const getUniqueCollections = async (userId) => {
    const allBoards = await getBoards(userId);
    const collections = new Set();
    allBoards.forEach((board) => {
        if (board.collection) {
            collections.add(board.collection);
        }
    });
    return Array.from(collections).sort();
};

const reorderBoards = async (boardPositions) => {
    const response = await api.put(`/boards/reorder`, { positions: boardPositions });
    return response.data;
};

const boardService = {
    getBoards,
    getBoard,
    createBoard,
    updateBoard,
    deleteBoard,
    exportBoardJson,
    exportBoardCsv,
    exportBoardMarkdown,
    importBoardJson,
    importBoardCsv,
    exportAllBoardsMarkdownAsZip,
    getUniqueCollections,
    reorderBoards,
};

export default boardService;
